import {
  Close,
  Delete as DeleteIcon,
  ManageSearch,
  Save,
} from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  type MenuItemProps,
  type MenuProps,
  Popover,
  TextField,
  Typography,
} from '@mui/material'
import { type FormEvent, useMemo, useRef, useState } from 'react'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import { useSavedSearch } from '@/features/search/hooks/useSavedSearch'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useToast } from '@/features/toasts/useToast'
import { useAppSelector } from '@/redux/hooks'
import { encodeCriteriaString } from '@/features/search/maps/criteriaString'

import { SavedSearchManager } from './SavedSearchManager'

type Props = {} & MenuProps

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'save search',
  formId: 'save-search',
  formLocation: 'popup',
  formDestination: 'emc/savedSearch',
  formSubmitText: 'submit',
}

export const SavedSearchMenu = (props: Props) => {
  return (
    <Menu slotProps={{ list: { color: 'secondary' } }} {...props}>
      <SaveSearch closeParentMenu={props.onClose} />
      <Delete closeParentMenu={props.onClose} />
      <Divider />
      <Manage />
    </Menu>
  )
}

type MenuItemWithParentProps = MenuItemProps & {
  closeParentMenu?: MenuProps['onClose']
}

export const SaveSearch = (props: MenuItemWithParentProps) => {
  const { closeParentMenu, ...rest } = props
  const gaFormRef = useRef<GaFormHandle>(null)
  const ref = useRef(null)
  const { error: showErrorToast } = useToast()
  const {
    getCurrentSavedSearch,
    useNewSavedSearchMutation,
    setSelectedSavedSearch,
  } = useSavedSearch()

  const { selectors } = useSearch()
  const activeSearchOptions = useAppSelector(selectors.selectActiveSearchOptions)

  const savedSearch = getCurrentSavedSearch()
  const [name, setName] = useState(
    savedSearch ? `${savedSearch.name} Copy` : '',
  )

  const [saveSearch, request] = useNewSavedSearchMutation()
  const [isOpen, setOpen] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { markPending } = useDeferredGaSubmit(gaFormRef, request)

  const validate = () => {
    const errors: string[] = []

    if (!name) {
      errors.push('Required')
    }

    setValidationErrors(errors)
    return errors.length === 0
  }

  const menuItemText = useMemo(() => {
    if (savedSearch) {
      return 'Save as New'
    }

    return 'Save'
  }, [savedSearch])

  const handleClose = () => setOpen(false)
  const handleOpen = () => setOpen(true)
  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()

    if (!validate()) return

    const criteria = activeSearchOptions
      ? encodeCriteriaString(activeSearchOptions)
      : ''

    markPending()

    saveSearch({
      name,
      criteria,
    }).then((result) => {
      if ('error' in result) {
        showErrorToast('Unable to save search. Please try again.')
        return
      }

      const value = result.data
      setSelectedSavedSearch({
        ...value,
        name: value.name || name,
        criteria: value.criteria || criteria,
      })
      handleClose()
      if (closeParentMenu) closeParentMenu({}, 'backdropClick')
    })
  }

  return (
    <>
      <MenuItem
        ref={ref}
        selected={isOpen}
        onSelect={handleOpen}
        onClick={handleOpen}
        disabled={!activeSearchOptions}
        {...rest}
      >
        <ListItemIcon>
          <Save />
        </ListItemIcon>
        <ListItemText>
          <Typography>{menuItemText}</Typography>
        </ListItemText>
      </MenuItem>
      <Popover
        open={isOpen}
        onClose={handleClose}
        anchorEl={ref.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'center' }}
        transformOrigin={{ horizontal: 'right', vertical: 'center' }}
      >
        <GaForm
          ref={gaFormRef}
          {...baseGaFormProps}
          onSubmit={handleSubmit}
          formStatus={request.isError ? 'error' : 'success'}
          getFormContent={() => (name ? `Saved Search Name: ${name}` : null)}
          deferGaSubmit={true}
        >
          <Box
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation()
              }
            }} // keep menu from getting key strokes
          >
            <Grid container p={2} spacing={1} alignItems={'center'}>
              <Grid size={'grow'}>
                <TextField
                  fullWidth
                  label="Saved Search Name"
                  placeholder="Name"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                  }}
                  error={validationErrors.length > 0}
                  helperText={validationErrors[0] ?? ''}
                  autoFocus
                />
              </Grid>
              <Grid offset="auto">
                <Button disabled={request.isLoading} type="submit">
                  {request.isLoading && (
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  )}
                  {request.isLoading ? 'Saving...' : 'Submit'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GaForm>
      </Popover>
    </>
  )
}

const Delete = (props: MenuItemWithParentProps) => {
  const { closeParentMenu, ...rest } = props
  const {
    getCurrentSavedSearch,
    useDeleteSavedSearchMutation,
    setSelectedSavedSearch,
  } = useSavedSearch()
  const currentSavedSearch = getCurrentSavedSearch()
  const [deleteSavedSearch, request] = useDeleteSavedSearchMutation()

  const handleClick = () => {
    if (currentSavedSearch)
      deleteSavedSearch(currentSavedSearch.code)
        .unwrap()
        .then(() => setSelectedSavedSearch(null))
        .then(() => {
          if (closeParentMenu) closeParentMenu({}, 'backdropClick')
        })
        .catch((error) => {
          console.error('Failed to delete search:', error)
        })
  }

  return (
    <MenuItem
      disabled={!currentSavedSearch || request.isLoading}
      onClick={handleClick}
      {...rest}
    >
      <ListItemIcon>
        <DeleteIcon color="error" />
      </ListItemIcon>
      <ListItemText>
        <Typography color="error">Delete</Typography>
      </ListItemText>
    </MenuItem>
  )
}

const Manage = (props: MenuItemProps) => {
  const [open, setOpen] = useState(false)
  const handleClick = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  return (
    <>
      <MenuItem onClick={handleClick} {...props}>
        <ListItemIcon>
          <ManageSearch />
        </ListItemIcon>
        <ListItemText>
          <Typography>Manage All</Typography>
        </ListItemText>
      </MenuItem>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle flexGrow={1}>
          Manage Saved Searches
          <IconButton onClick={handleClose} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <SavedSearchManager />
        </DialogContent>
      </Dialog>
    </>
  )
}
